import React from 'react';
import {
  Search,
  ScanLine,
  Sparkles,
  BookOpen,
  ArrowRight,
  Globe,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { LazyBookCover } from '../../../../shared/components/LazyBookCover.js';
import { resolveCoverUrl } from '../../../../shared/utils/imageCompressor.js';

interface LandingHeroSectionProps {
  searchQuery: string;
  isSearching: boolean;
  isLandingAutocompleteOpen: boolean;
  landingSuggestions: any[];
  isLandingSuggestionsLoading: boolean;
  onSearchQueryChange: (query: string) => void;
  onAutocompleteOpenChange: (open: boolean) => void;
  onSearchSubmit: (e?: React.FormEvent) => void;
  onTriggerScanner: () => void;
  onSelectSuggestion: (item: any) => void;
  onExploreDirectory?: () => void;
}

export const LandingHeroSection: React.FC<LandingHeroSectionProps> = ({
  searchQuery,
  isSearching,
  isLandingAutocompleteOpen,
  landingSuggestions,
  isLandingSuggestionsLoading,
  onSearchQueryChange,
  onAutocompleteOpenChange,
  onSearchSubmit,
  onTriggerScanner,
  onSelectSuggestion,
  onExploreDirectory,
}) => {
  const [placeholderText, setPlaceholderText] = React.useState("Cari UIBN atau judul buku...");

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setPlaceholderText("Cari UIBN atau judul buku...");
      } else {
        setPlaceholderText("Ketik kode UIBN (UIBN-979-...) atau judul buku...");
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="relative text-center max-w-5xl 2xl:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Badge */}
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs sm:text-sm 2xl:text-base font-semibold mb-5 shadow-2xs">
        <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 text-indigo-600" />
        <span>Platform Katalog & Identifikasi Buku Nasional</span>
      </div>

      {/* Hero Title */}
      <h1 className="text-3xl sm:text-5xl lg:text-6xl 2xl:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.15]">
        Universal Indonesian <br className="hidden sm:block" />
        <span className="text-indigo-600">
          Book Number (UIBN)
        </span>
      </h1>

      {/* Subtitle */}
      <p className="mt-5 text-sm sm:text-base md:text-lg 2xl:text-xl text-slate-600 max-w-3xl 2xl:max-w-4xl mx-auto leading-relaxed">
        Solusi modern identifikasi buku nasional. Terbitkan kode UIBN instan, lengkapi karya dengan barcode atau QR code verifikasi resmi, dan amankan orisinalitas buku Anda dalam satu katalog terpadu untuk memperluas jangkauan pembaca.
      </p>

      {/* Central Search & Scan Box */}
      <div className="mt-8 sm:mt-10 max-w-3xl 2xl:max-w-4xl mx-auto relative">
        <form
          onSubmit={(e) => {
            onAutocompleteOpenChange(false);
            onSearchSubmit(e);
          }}
          className="p-2 sm:p-2.5 2xl:p-3 bg-white rounded-2xl 2xl:rounded-3xl border border-slate-300/80 shadow-lg shadow-indigo-950/5 flex flex-col sm:flex-row items-center gap-2"
        >
          <div className="relative flex-1 w-full flex items-center">
            <Search className="w-5 h-5 2xl:w-6 2xl:h-6 text-slate-400 absolute left-3.5 2xl:left-4 pointer-events-none" />
            <input
              id="input-landing-search"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                onSearchQueryChange(e.target.value);
                onAutocompleteOpenChange(true);
              }}
              onFocus={() => {
                if (searchQuery.trim().length >= 1) onAutocompleteOpenChange(true);
              }}
              placeholder={placeholderText}
              className="w-full pl-11 2xl:pl-12 pr-4 py-2.5 2xl:py-3 text-sm sm:text-base 2xl:text-lg border-0 focus:outline-hidden focus:ring-0 text-slate-900 placeholder:text-slate-400 min-h-[44px]"
            />
          </div>

          <div className="flex items-stretch gap-2 w-full sm:w-auto shrink-0">
            <button
              id="btn-trigger-scanner"
              type="button"
              onClick={onTriggerScanner}
              className="flex-1 sm:flex-none px-4 sm:px-6 2xl:px-7 py-3 2xl:py-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl 2xl:rounded-2xl text-xs sm:text-sm 2xl:text-base font-bold flex items-center justify-center gap-2 transition cursor-pointer min-h-[48px] sm:min-h-[44px]"
              title="Pindai dengan Kamera"
            >
              <ScanLine className="w-4 h-4 2xl:w-5 2xl:h-5 text-indigo-600 shrink-0" />
              <span>Pindai</span>
            </button>

            <button
              id="btn-submit-search"
              type="submit"
              disabled={isSearching}
              className="flex-1 sm:flex-none px-4 sm:px-6 2xl:px-7 py-3 2xl:py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl 2xl:rounded-2xl text-xs sm:text-sm 2xl:text-base font-bold flex items-center justify-center gap-2 shadow-xs transition disabled:opacity-50 cursor-pointer min-h-[48px] sm:min-h-[44px]"
            >
              <Search className="w-4 h-4 2xl:w-5 2xl:h-5 text-white shrink-0 sm:hidden lg:block" />
              <span>{isSearching ? 'Mencari...' : 'Cari Buku'}</span>
            </button>
          </div>
        </form>

        {/* Autocomplete Dropdown Popover */}
        {isLandingAutocompleteOpen && searchQuery.trim().length >= 1 && (
          <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden text-left divide-y divide-slate-100">
            <div className="px-4 py-2.5 bg-slate-50 text-xs 2xl:text-sm font-semibold text-slate-600 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-indigo-600" />
                Saran Pencarian FTS5 Real-Time
              </span>
              <span className="text-[11px] 2xl:text-xs text-slate-400">{landingSuggestions.length} hasil</span>
            </div>

            {isLandingSuggestionsLoading && landingSuggestions.length === 0 ? (
              <div className="p-4 text-center text-xs sm:text-sm text-slate-400">
                Mencari rekomendasi judul buku...
              </div>
            ) : landingSuggestions.length === 0 ? (
              <div className="p-4 text-center text-xs sm:text-sm text-slate-500">
                Tidak ditemukan rekomendasi untuk kata kunci ini.
              </div>
            ) : (
              <div className="py-1 max-h-80 overflow-y-auto">
                {landingSuggestions.map((item, idx) => (
                  <button
                    key={`landing-hero-sug-${item.id || item.uibn || idx}-${idx}`}
                    type="button"
                    onClick={() => onSelectSuggestion(item)}
                    className="w-full px-4 py-2.5 hover:bg-indigo-50/80 transition flex items-center justify-between gap-3 text-left cursor-pointer border-b border-slate-50 last:border-b-0"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <LazyBookCover
                        coverUrl={resolveCoverUrl(item.coverUrl)}
                        title={item.title}
                        className="w-9 h-12 sm:w-10 sm:h-14 rounded-md shrink-0 shadow-2xs"
                        targetWidth={160}
                        quality={80}
                        priority={true}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-bold text-slate-900 truncate">{item.title}</p>
                        <p className="text-[11px] sm:text-xs text-slate-500 truncate">
                          {item.authorName} • <span className="font-mono text-indigo-600 font-semibold">{item.uibn}</span>
                        </p>
                      </div>
                    </div>

                    <span className="text-[10px] sm:text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full shrink-0">
                      {item.category}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Direct link to Public Directory for unauthenticated visitors */}
      <div className="mt-5 flex items-center justify-center gap-2 text-xs sm:text-sm 2xl:text-base text-slate-600 font-medium">
        <Globe className="w-4 h-4 2xl:w-5 2xl:h-5 text-indigo-600 shrink-0" />
        <span>Atau jelajahi daftar penerbit & penulis:</span>
        <button
          type="button"
          onClick={onExploreDirectory}
          className="font-bold text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1 cursor-pointer"
        >
          <span>Direktori Publik &rarr;</span>
        </button>
      </div>
    </div>
  );
};
